import { createContext, useContext, useEffect, useRef, useState } from 'react';
import adminAuthService from '../services/adminAuthService';

const AdminAuthContext = createContext(null);

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const refreshPromiseRef = useRef(null);

  // Check if admin is authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Fetch admin context from cookie (no localStorage check)
        // Cookie è HttpOnly, solo il backend può verificarlo
        try {
          let currentAdmin;
          try {
            currentAdmin = await adminAuthService.getAdminContext();
          } catch (err) {
            if (err.status !== 401) throw err;
            await adminAuthService.refreshSession();
            currentAdmin = await adminAuthService.getAdminContext();
          }
          setAdmin(currentAdmin);
          console.log('✅ Admin context loaded via cookie:', currentAdmin.email);
        } catch (err) {
          if (err.status !== 401) {
            setError(err.message || 'Impossibile verificare la sessione admin');
          }
          setAdmin(null);
        }
      } catch (err) {
        console.error('Auth check error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  useEffect(() => {
    if (!admin) return undefined;

    let lastRefreshAt = Date.now();
    let disposed = false;
    const refresh = async () => {
      if (disposed || refreshPromiseRef.current || Date.now() - lastRefreshAt < 10 * 60 * 1000) return;
      refreshPromiseRef.current = adminAuthService.refreshSession();
      try {
        await refreshPromiseRef.current;
        lastRefreshAt = Date.now();
      } catch (err) {
        if (err.response?.status === 401 || err.status === 401) {
          setAdmin(null);
        } else {
          console.error('Admin session refresh failed:', err);
        }
      } finally {
        refreshPromiseRef.current = null;
      }
    };

    const intervalId = window.setInterval(refresh, 60 * 1000);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      disposed = true;
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [admin]);

  const login = async (email, password) => {
    try {
      setError(null);
      setLoading(true);
      const result = await adminAuthService.login(email, password);
      if (result.admin) setAdmin(result.admin);
      return result;
    } catch (err) {
      setError(err.message || 'Login fallito');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const verifyMfa = async (code, newPassword) => {
    try {
      setError(null);
      setLoading(true);
      const result = await adminAuthService.verifyMfa(code, newPassword);
      setAdmin(result.admin);
      return result;
    } catch (err) {
      setError(err.message || 'Verifica MFA non riuscita');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (profile) => {
    const result = await adminAuthService.updateProfile(profile);
    setAdmin(result.admin);
    return result.admin;
  };

  const logout = async () => {
    try {
      await adminAuthService.logout();
    } finally {
      setAdmin(null);
      console.log('✅ Admin logged out locally');
    }
  };

  const value = {
    admin,
    loading,
    error,
    isAuthenticated: !!admin,
    login,
    verifyMfa,
    updateProfile,
    logout
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};
